import cn from 'classnames';
import { FC, MouseEvent, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button, Chip } from '@/shared/ui';

import {
  consultationStatusMessages,
  transcriptorMessages,
  TranscriptorConsultation,
} from '../../config';
import { hasNoSpeechDetected, isUploadNotCompleted } from '../../lib';

import styles from './ConsultationStatus.module.scss';

type ConsultationStatusProps = {
  consultation: TranscriptorConsultation;
  isStale?: boolean;
  className?: string;
  onForceStop?: (consultationID: string) => void;
};

// Renders nothing for a completed recording so callers can show the summary.
export const ConsultationStatus: FC<ConsultationStatusProps> = ({
  consultation,
  isStale,
  className,
  onForceStop,
}) => {
  const { formatMessage } = useIntl();
  const [isForceStopLoading, setIsForceStopLoading] = useState(false);

  const handleForceStop = (event: MouseEvent) => {
    event.stopPropagation();

    if (onForceStop) {
      setIsForceStopLoading(true);
      onForceStop(consultation.id);
    }
  };

  if (hasNoSpeechDetected(consultation)) {
    return (
      <Chip
        label={formatMessage(transcriptorMessages.statusNoSpeechDetected)}
        className={cn(styles.statusChip, styles.statusNoSpeech, className)}
      />
    );
  }

  if (isUploadNotCompleted(consultation)) {
    return (
      <Chip
        label={formatMessage(transcriptorMessages.statusUploadNotCompleted)}
        className={cn(styles.statusChip, className)}
      />
    );
  }

  if (isStale) {
    return (
      <div className={cn(styles.staleSection, className)}>
        <p className={cn('p3', styles.staleMessage)}>
          <FormattedMessage {...transcriptorMessages.staleRecordingMessage} />
        </p>
        <Button
          variant="primary"
          size="small"
          onClick={handleForceStop}
          disabled={isForceStopLoading}
          className={styles.staleButton}
        >
          <FormattedMessage
            {...transcriptorMessages.staleRecordingStopButton}
          />
        </Button>
      </div>
    );
  }

  const { status } = consultation;
  const statusMessage = consultationStatusMessages[status];

  if (!statusMessage || status === 'succeeded') {
    return null;
  }

  return (
    <Chip
      label={formatMessage(statusMessage)}
      className={cn(
        styles.statusChip,
        (status === 'processing' || status === 'created') &&
          styles.statusProcessing,
        status === 'recording' && styles.statusRecording,
        className,
      )}
    />
  );
};
